import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { setI18nLocale } from '@/i18n'

const SIDEBAR_KEY = 'adminSidebarCollapsed'
const LOCALE_KEY = 'adminLocale' // owned by setI18nLocale; UI store reads on init
const THEME_KEY = 'bzuco_theme'

export type Theme = 'auto' | 'light' | 'dark'

function safeGetItem(key: string): string | null {
    try {
        return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null
    } catch {
        return null
    }
}

function safeSetItem(key: string, value: string): void {
    try {
        if (typeof window !== 'undefined') window.localStorage.setItem(key, value)
    } catch {
        // ignore
    }
}

function applyTheme(resolved: 'light' | 'dark'): void {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', resolved)
    }
}

function readSystemPref(): 'light' | 'dark' {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStoredTheme(): Theme {
    const raw = safeGetItem(THEME_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'auto') return raw
    return 'auto'
}

export interface BreadcrumbItem {
    label: string
    to?: string
}

export const useUiStore = defineStore('ui', () => {
    const sidebarCollapsed = ref<boolean>(safeGetItem(SIDEBAR_KEY) === '1')
    // Off-canvas drawer state for mobile (< lg). Not persisted — always starts closed.
    const mobileDrawerOpen = ref<boolean>(false)
    const breadcrumbs = ref<BreadcrumbItem[]>([])
    const locale = ref<string>(safeGetItem(LOCALE_KEY) ?? 'cs')

    const theme = ref<Theme>(readStoredTheme())
    const systemPref = ref<'light' | 'dark'>(readSystemPref())

    const resolvedTheme = computed<'light' | 'dark'>(() =>
        theme.value === 'auto' ? systemPref.value : theme.value
    )

    function toggleSidebar() {
        sidebarCollapsed.value = !sidebarCollapsed.value
        safeSetItem(SIDEBAR_KEY, sidebarCollapsed.value ? '1' : '0')
    }

    function openMobileDrawer() {
        mobileDrawerOpen.value = true
    }
    function closeMobileDrawer() {
        mobileDrawerOpen.value = false
    }
    function toggleMobileDrawer() {
        mobileDrawerOpen.value = !mobileDrawerOpen.value
    }

    function setBreadcrumbs(items: BreadcrumbItem[]) {
        breadcrumbs.value = items
    }

    function setLocale(code: string) {
        locale.value = code
        setI18nLocale(code)
    }

    function setTheme(next: Theme) {
        theme.value = next
        safeSetItem(THEME_KEY, next)
    }

    // React to system pref changes for 'auto'
    if (typeof window !== 'undefined' && window.matchMedia) {
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const onChange = (e: MediaQueryListEvent) => {
            systemPref.value = e.matches ? 'dark' : 'light'
        }
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange)
        }
    }

    // Apply on any resolved change. Immediate ensures consistent state
    // even though main.ts has already set data-theme before mount.
    watch(resolvedTheme, applyTheme, { immediate: true })

    return {
        sidebarCollapsed,
        mobileDrawerOpen,
        breadcrumbs,
        locale,
        theme,
        resolvedTheme,
        toggleSidebar,
        openMobileDrawer,
        closeMobileDrawer,
        toggleMobileDrawer,
        setBreadcrumbs,
        setLocale,
        setTheme
    }
})
